import { ALERT_SEVERITIES, ALERT_SEVERITY_LABELS, SEVERITY_MAP } from '@kentik/ui-shared/alerting/constants';
import type { RBACString } from '@kentik/ui-shared/rbac/constants';
import { CorrelationConfidenceLevel } from '@kentik/schema/gen/ts/kentik/alerting/correlation/v202609/correlation_pb';
import type {
  ConfidenceBand,
  CorrelationAckKey,
  CorrelationFilterValues,
  CorrelationStatusKey,
  Granularity
} from './types';

/** Correlations are gated by the alert permissions, as the schema's `method_permission`s are. */
export const CORRELATION_PERMISSIONS = {
  read: 'alerting.alerts::read',
  ack: 'alerting.alerts::ack',
  clear: 'alerting.alerts::clear'
} as const satisfies Record<string, RBACString>;

export const CONFIDENCE_BAND_BY_LEVEL: Record<CorrelationConfidenceLevel, ConfidenceBand> = {
  [CorrelationConfidenceLevel.UNSPECIFIED]: 'Unscored',
  [CorrelationConfidenceLevel.LOW]: 'Low',
  [CorrelationConfidenceLevel.MEDIUM]: 'Medium',
  [CorrelationConfidenceLevel.HIGH]: 'High'
};

export const CONFIDENCE_LEVEL_BY_BAND: Record<ConfidenceBand, CorrelationConfidenceLevel> = {
  Unscored: CorrelationConfidenceLevel.UNSPECIFIED,
  Low: CorrelationConfidenceLevel.LOW,
  Medium: CorrelationConfidenceLevel.MEDIUM,
  High: CorrelationConfidenceLevel.HIGH
};

/** Tag intent, and value color, for each confidence band. */
export const CONFIDENCE_BAND_INTENT: Record<ConfidenceBand, 'success' | 'warning' | 'danger' | undefined> = {
  High: 'success',
  Medium: 'warning',
  Low: 'danger',
  Unscored: undefined
};

export const CONFIDENCE_BAND_ORDER: ConfidenceBand[] = ['High', 'Medium', 'Low', 'Unscored'];

export const STATUS_ORDER: CorrelationStatusKey[] = ['open', 'cleared'];

export const STATUS_LABELS: Record<CorrelationStatusKey, string> = {
  open: 'Active',
  cleared: 'Cleared'
};

export const ACK_ORDER: CorrelationAckKey[] = ['unacked', 'acked'];

export const ACK_LABELS: Record<CorrelationAckKey, string> = {
  unacked: 'Not Acked',
  acked: 'Acked'
};

/** Worst first, including Clear. */
export const SEVERITY_LABEL_ORDER: string[] = [...ALERT_SEVERITIES, SEVERITY_MAP.CLEAR.STANDARD_ALERT].map(
  (severity) => ALERT_SEVERITY_LABELS[severity as keyof typeof ALERT_SEVERITY_LABELS]
);

/** Group ordering for the list, by attention rather than alphabetically. */
export const GROUP_ORDER: Record<string, readonly string[]> = {
  statusKey: STATUS_ORDER,
  severityLabel: SEVERITY_LABEL_ORDER,
  ackKey: ACK_ORDER,
  confidenceBand: CONFIDENCE_BAND_ORDER
};

/** `entityType` of comments left on a correlation. */
export const CORRELATION_COMMENT_ENTITY_TYPE = 'correlation';

export const DEFAULT_LOOKBACK_SECONDS = 86_400;

/** The correlations list's filters before the user changes any. */
export const DEFAULT_CORRELATION_FILTERS: CorrelationFilterValues = {
  statuses: ['open'],
  lookback: DEFAULT_LOOKBACK_SECONDS
};

/** Server field names, as the list columns' `sortField`s are. */
export const DEFAULT_CORRELATION_SORT: { field: string; direction: 'asc' | 'desc' } = {
  field: 'updated_at',
  direction: 'desc'
};

export const CORRELATIONS_PER_PAGE_DEFAULT = 25;

export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

export const GRANULARITIES: Granularity[] = [
  { value: '1m', label: '1 min buckets', ms: MINUTE, interval: '1 minute' },
  { value: '5m', label: '5 min buckets', ms: 5 * MINUTE, interval: '5 minutes' },
  { value: '15m', label: '15 min buckets', ms: 15 * MINUTE, interval: '15 minutes' },
  { value: '30m', label: '30 min buckets', ms: 30 * MINUTE, interval: '30 minutes' },
  { value: '1h', label: '1 hour buckets', ms: HOUR, interval: '1 hour' },
  { value: '2h', label: '2 hour buckets', ms: 2 * HOUR, interval: '2 hours' },
  { value: '3h', label: '3 hour buckets', ms: 3 * HOUR, interval: '3 hours' },
  { value: '6h', label: '6 hour buckets', ms: 6 * HOUR, interval: '6 hours' },
  { value: '12h', label: '12 hour buckets', ms: 12 * HOUR, interval: '12 hours' },
  { value: '1d', label: '1 day buckets', ms: DAY, interval: '1 day' },
  { value: '2d', label: '2 day buckets', ms: 2 * DAY, interval: '2 days' }
];
